import { useState, useCallback, useRef } from 'react';
import * as svc from '../services/prospectingService';
import type { ProspectBatch, Prospect, ProspectingCriteria, ConnectorSelection } from '../services/prospectingService';

export const useProspecting = () => {
  const [batches, setBatches]           = useState<ProspectBatch[]>([]);
  const [currentBatch, setCurrentBatch] = useState<ProspectBatch | null>(null);
  const [loading, setLoading]           = useState(false);
  const [approving, setApproving]       = useState(false);
  const [stagedApprovals, setStagedApprovals]   = useState<Set<string>>(new Set());
  const [stagedRejections, setStagedRejections] = useState<Set<string>>(new Set());
  const [editedProspects, setEditedProspects]   = useState<Record<string, Partial<Prospect>>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try   { const r = await svc.getBatches(); setBatches(r.data); }
    finally { setLoading(false); }
  }, []);

  const fetchBatch = useCallback(async (id: string) => {
    setLoading(true);
    try   { const b = await svc.getBatch(id); setCurrentBatch(b); return b; }
    finally { setLoading(false); }
  }, []);

  const startPolling = useCallback((batchId: string, onComplete: (status: string) => void) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await svc.getBatchStatus(batchId);
        if (s.status !== 'RUNNING' && s.status !== 'PENDING') {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          const batch = await svc.getBatch(batchId);
          setCurrentBatch(batch);
          onComplete(s.status);
        }
      } catch { /* keep polling */ }
    }, 10000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const resetReviewState = useCallback(() => {
    setStagedApprovals(new Set());
    setStagedRejections(new Set());
    setEditedProspects({});
  }, []);

  const stageApproval = useCallback((id: string) => {
    setStagedApprovals(p => new Set([...p, id]));
    setStagedRejections(p => { const n = new Set(p); n.delete(id); return n; });
  }, []);

  const stageRejection = useCallback((id: string) => {
    setStagedRejections(p => new Set([...p, id]));
    setStagedApprovals(p => { const n = new Set(p); n.delete(id); return n; });
  }, []);

  const unstage = useCallback((id: string) => {
    setStagedApprovals(p => { const n = new Set(p); n.delete(id); return n; });
    setStagedRejections(p => { const n = new Set(p); n.delete(id); return n; });
  }, []);

  const stageEdit = useCallback((id: string, fields: Partial<Prospect>) => {
    setEditedProspects(p => ({ ...p, [id]: { ...p[id], ...fields } }));
    setStagedApprovals(p => new Set([...p, id]));
    setStagedRejections(p => { const n = new Set(p); n.delete(id); return n; });
  }, []);

  const submitApprovals = useCallback(async (batchId: string) => {
    if (stagedApprovals.size === 0 && stagedRejections.size === 0) return null;
    setApproving(true);
    try {
      // Apply edits first
      for (const [id, fields] of Object.entries(editedProspects)) {
        if (stagedApprovals.has(id)) {
          await svc.updateProspect(id, { ...fields, reviewStatus: 'EDITED' });
        }
      }
      // Rejections
      for (const id of stagedRejections) {
        await svc.updateProspect(id, { reviewStatus: 'REJECTED' });
      }
      // Bulk approve
      const result = stagedApprovals.size > 0
        ? await svc.approveBatch(batchId, Array.from(stagedApprovals))
        : { approved: 0, failed: 0, results: [], errors: [], message: 'No approvals' };

      const updated = await svc.getBatch(batchId);
      setCurrentBatch(updated);
      resetReviewState();
      return result;
    } finally {
      setApproving(false);
    }
  }, [stagedApprovals, stagedRejections, editedProspects, resetReviewState]);

  return {
    batches, currentBatch, loading, approving,
    stagedApprovals, stagedRejections, editedProspects,
    fetchBatches, fetchBatch,
    startPolling, stopPolling, resetReviewState,
    stageApproval, stageRejection, unstage, stageEdit,
    submitApprovals,
  };
};
